import React, { useEffect, useState } from "react";

import LoadingSpinner from "../../LoadingSpinner";
import Modal from "../../Modal";
import { Button } from "../../ui/button";
import {
  OVERRIDABLE_VIEW_CAPS,
  type AdminUser,
  type PermissionState,
  type ScuGrant,
  type SystemRole,
  fetchAdminUser,
  setUserPermissionOverride,
  updateAdminUser,
} from "../../../services/adminApi";
import { inputCls, fullName } from "./shared";
import { Field } from "./Field";
import { VisibilityEditor } from "./VisibilityEditor";
import { UserNotificationsSection } from "./UserNotificationsSection";
import { ResetPasswordSection } from "./ResetPasswordSection";

export const EditUserModal: React.FC<{
  token: string;
  uid: number;
  roles: SystemRole[];
  canManageViewPerms: boolean;
  onClose: () => void;
  onDone: () => void;
}> = ({ token, uid, roles, canManageViewPerms, onClose, onDone }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [roleId, setRoleId] = useState<number | "">("");
  // Per-user view-cap overrides (pricing + dashboard tiles): the states as
  // loaded (to detect a change on save), and the current 3-way selection per
  // cap (1 = Always, 0 = Never, null = role default).
  const [permStates, setPermStates] = useState<Record<string, PermissionState>>({});
  const [permSel, setPermSel] = useState<Record<string, 0 | 1 | null>>({});
  // Name + email are editable here because email is not cosmetic: it is where
  // notification subscriptions and the Admin > Mail test send, and a blank one
  // means the user silently receives nothing.
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState<"derived" | "explicit">("derived");
  const [grants, setGrants] = useState<Map<string, { read: boolean; write: boolean }>>(new Map());
  // Target user's effective SCUs — drives the manual-checkout SmartBox picker in
  // the Notifications section so the admin only offers boxes the RECIPIENT can
  // see (the server re-checks against the target's scope regardless).
  const [effectiveScus, setEffectiveScus] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminUser(uid, token)
      .then((d) => {
        setUser(d.user);
        setRoleId(d.user.role_id ?? "");
        setFirst(d.user.first_name ?? "");
        setLast(d.user.last_name ?? "");
        setEmail(d.user.email ?? "");
        setMode(d.user.scu_access_mode);
        setGrants(new Map(d.grants.map((g) => [g.scu_name, { read: g.can_read, write: g.can_write }])));
        setEffectiveScus(d.effective_scus);
        const states = d.view_permissions ?? {};
        setPermStates(states);
        setPermSel(
          Object.fromEntries(
            OVERRIDABLE_VIEW_CAPS.map((c) => [c.key, states[c.key]?.override ?? null]),
          ),
        );
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [uid, token]);

  // System roles match by company_type (as before); custom roles are
  // company-scoped, so offer only those whose company_id matches this user.
  const systemRoles = roles.filter(
    (r) => !r.is_custom && (!user?.company_type || r.company_type === user.company_type),
  );
  const customRoles = roles.filter(
    (r) => r.is_custom && user != null && r.company_id === user.company_id,
  );

  const save = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const body: Parameters<typeof updateAdminUser>[1] = { scu_access_mode: mode };
      if (roleId !== "") body.role_id = Number(roleId);
      // Only send what actually changed, so an unrelated save can't clear a
      // field and the audit row names the real edit.
      if (first !== (user?.first_name ?? "")) body.first_name = first.trim();
      if (last !== (user?.last_name ?? "")) body.last_name = last.trim();
      if (email !== (user?.email ?? "")) {
        const next = email.trim();
        if (next && !next.includes("@")) {
          setErr("A valid email is required.");
          setBusy(false);
          return;
        }
        body.email = next;
      }
      body.grants = Array.from(grants, ([scu_name, g]): ScuGrant => ({
        scu_name,
        can_read: g.read,
        can_write: g.write,
      }));
      await updateAdminUser(uid, body, token);
      // View-cap overrides are a separate endpoint; only call for the caps that
      // actually changed.
      if (canManageViewPerms) {
        for (const c of OVERRIDABLE_VIEW_CAPS) {
          const next = permSel[c.key] ?? null;
          if (next !== (permStates[c.key]?.override ?? null)) {
            await setUserPermissionOverride(uid, c.key, next, token);
          }
        }
      }
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={user ? `Edit ${fullName(user)}` : "Edit user"} widthClass="max-w-lg">
      {!user ? (
        <LoadingSpinner label="loading user" />
      ) : (
        <div className="space-y-4">
          {err && <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input
                className={inputCls}
                value={first}
                onChange={(e) => setFirst(e.target.value)}
              />
            </Field>
            <Field label="Last name">
              <input
                className={inputCls}
                value={last}
                onChange={(e) => setLast(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Email">
            <input
              className={inputCls}
              type="email"
              value={email}
              autoComplete="off"
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="text-[11px] text-secondary-500 mt-0.5">
              Where this user&apos;s notifications and the Admin &rsaquo; Mail test
              email are sent. If the address is already used by another account,
              use an alias like{" "}
              <code>name+customer@example.com</code> &mdash; it delivers to the
              same inbox.
            </div>
          </Field>
          <Field label="Role">
            <select
              className={inputCls + " bg-white"}
              value={roleId}
              onChange={(e) => setRoleId(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <option value="">(no role)</option>
              {systemRoles.map((r) => (
                <option key={r.role_id} value={r.role_id}>
                  {r.role_name}
                </option>
              ))}
              {customRoles.length > 0 && (
                <optgroup label="Custom roles">
                  {customRoles.map((r) => (
                    <option key={r.role_id} value={r.role_id}>
                      {r.role_name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </Field>
          <div>
            <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
              Box visibility
            </span>
            <VisibilityEditor mode={mode} setMode={setMode} grants={grants} setGrants={setGrants} />
          </div>
          {canManageViewPerms && (
            <div>
              <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                View permissions
              </span>
              <div className="space-y-2">
                {OVERRIDABLE_VIEW_CAPS.map((c) => {
                  const state = permStates[c.key];
                  const sel = permSel[c.key] ?? null;
                  return (
                    <div key={c.key} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm">{c.label}</div>
                        <div className="text-[11px] text-secondary-500">
                          Currently{" "}
                          <strong>{state?.effective ? "visible" : "hidden"}</strong>
                          {state && state.override === null && " (from role)"}
                        </div>
                      </div>
                      <select
                        className="border border-secondary-300 rounded px-2 py-1 text-sm bg-white shrink-0"
                        value={sel === null ? "" : String(sel)}
                        onChange={(e) =>
                          setPermSel((prev) => ({
                            ...prev,
                            [c.key]:
                              e.target.value === ""
                                ? null
                                : (Number(e.target.value) as 0 | 1),
                          }))
                        }
                      >
                        <option value="">Default (role)</option>
                        <option value="1">Always</option>
                        <option value="0">Never</option>
                      </select>
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-secondary-500 mt-1">
                Overrides which tiles &amp; views this user sees, regardless of role.
                Takes effect on their next login.
              </div>
            </div>
          )}
          <div>
            <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
              Notifications
            </span>
            <UserNotificationsSection uid={uid} token={token} effectiveScus={effectiveScus} />
          </div>
          <div>
            <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
              Password
            </span>
            <ResetPasswordSection uid={uid} token={token} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void save()}>
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
